import {
  useMutation,
  useQueryClient,
  useSuspenseQuery,
} from "@tanstack/react-query";
import { createFileRoute, getRouteApi, notFound } from "@tanstack/react-router";
import { ExternalLink, ShieldCheck } from "lucide-react";
import { useState } from "react";
import { z } from "zod";

import {
  ConfirmClaimsDialog,
  NotConfirmDialog,
  type DialogClaim,
} from "~/components/cco-claims/decision-dialogs";
import { Button, ButtonLink } from "~/components/ui/button";
import { Card } from "~/components/ui/card";
import { Checkbox } from "~/components/ui/checkbox";
import { EmptyState } from "~/components/ui/empty-state";
import { PageHeader } from "~/components/ui/page-title";
import { Tabs, TabsList, TabsTrigger } from "~/components/ui/tabs";
import { cn } from "~/lib/cn";
import {
  decideOrganizationClaimsFn,
  organizationClaimsQueryOptions,
} from "~/lib/functions/cco-claims";
import { ClaimVerificationState } from "~/lib/schemas/db-enums";
import {
  claimKey,
  verifiedByText,
  type ClaimDecision,
  type OrganizationClaim,
} from "~/lib/shared/cco-claims";

import { OrganizationMark } from "../-organization-mark";

const organizationRoute = getRouteApi("/_user/my-organization/$slug");

// The overview's numbers open this page on a tab and a filter.
const claimsSearchSchema = z.object({
  tab: z.nativeEnum(ClaimVerificationState).optional().catch(undefined),
  /** "membership" or a program id. */
  filter: z.string().min(1).optional().catch(undefined),
});

export const Route = createFileRoute("/_user/my-organization/$slug/claims")({
  staticData: { title: "Coach claims" },
  validateSearch: claimsSearchSchema,
  loader: async ({ context, params }) => {
    const claims = await context.queryClient.ensureQueryData(
      organizationClaimsQueryOptions(params.slug),
    );
    // Closed while the switch is off, or for someone who doesn't represent
    // the organization.
    if (!claims) throw notFound();
  },
  component: function CoachClaimsRoute(): React.JSX.Element {
    const search = Route.useSearch();
    // The tab and filter seed local state, so a new link remounts the page.
    return <CoachClaimsPage key={`${search.tab}:${search.filter}`} />;
  },
});

type Tab = ClaimVerificationState;

const TABS: { value: Tab; label: string }[] = [
  { value: ClaimVerificationState.Waiting, label: "To review" },
  { value: ClaimVerificationState.Verified, label: "Confirmed" },
  { value: ClaimVerificationState.NotConfirmed, label: "Not confirmed" },
];

/** "all", "membership" or a program id. */
type Filter = string;

const monthYear = new Intl.DateTimeFormat("en-US", {
  month: "short",
  year: "numeric",
  timeZone: "UTC",
});

function sinceText(claim: OrganizationClaim): string {
  if (claim.kind === "program") {
    return claim.certificationDate
      ? `Certified ${monthYear.format(new Date(claim.certificationDate))}`
      : "No date given";
  }
  return `Added ${monthYear.format(new Date(claim.addedAt))}`;
}

function toDialogClaim(claim: OrganizationClaim): DialogClaim {
  return {
    key: claimKey(claim),
    coachName: claim.coachName,
    label: claim.label,
  };
}

function CoachClaimsPage(): React.JSX.Element {
  const { slug } = Route.useParams();
  const search = Route.useSearch();
  const { organization } = organizationRoute.useLoaderData();
  const queryClient = useQueryClient();
  const options = organizationClaimsQueryOptions(slug);
  const { data } = useSuspenseQuery(options);
  const claims = data?.claims ?? [];
  const programs = data?.programs ?? [];

  const [tab, setTab] = useState<Tab>(
    search.tab ?? ClaimVerificationState.Waiting,
  );
  const [filter, setFilter] = useState<Filter>(search.filter ?? "all");
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [confirming, setConfirming] = useState<OrganizationClaim[]>([]);
  const [rejecting, setRejecting] = useState<OrganizationClaim | null>(null);

  const decide = useMutation({
    mutationFn: (input: {
      claims: OrganizationClaim[];
      decision: ClaimDecision;
      verificationDate: string | null;
      note: string | null;
    }) =>
      decideOrganizationClaimsFn({
        data: {
          slug,
          claims: input.claims.map(({ kind, coachId, targetId }) => ({
            kind,
            coachId,
            targetId,
          })),
          decision: input.decision,
          verificationDate: input.verificationDate,
          note: input.note,
        },
      }),
    onSuccess: async () => {
      setSelected(new Set());
      setConfirming([]);
      setRejecting(null);
      await queryClient.invalidateQueries({ queryKey: options.queryKey });
    },
  });

  const matchesFilter = (claim: OrganizationClaim): boolean =>
    filter === "all" ||
    (filter === "membership"
      ? claim.kind === "membership"
      : claim.kind === "program" && claim.targetId === filter);

  const counts = new Map<Tab, number>();
  for (const claim of claims) {
    if (!matchesFilter(claim)) continue;
    counts.set(claim.state, (counts.get(claim.state) ?? 0) + 1);
  }

  const visible = claims.filter(
    (claim) => claim.state === tab && matchesFilter(claim),
  );
  const selectedClaims = visible.filter((c) => selected.has(claimKey(c)));

  const chips: { value: Filter; label: string }[] = [
    { value: "all", label: "All" },
    { value: "membership", label: "Membership" },
    ...programs.map((program) => ({ value: program.id, label: program.name })),
  ];

  const toggle = (key: string): void => {
    setSelected((previous) => {
      const next = new Set(previous);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const header = (
    <div className="flex items-start gap-4">
      <OrganizationMark
        name={organization.name}
        logoUrl={organization.logoUrl}
      />
      <PageHeader
        className="flex-1"
        eyebrow={organization.name}
        title="Coach claims"
        description="Confirm which coaches on Dream Coach Match are members of your organization or completed its programs. You see what each coach's public profile already shows."
        actions={
          tab === ClaimVerificationState.Waiting &&
          selectedClaims.length > 0 ? (
            <Button
              variant="primary"
              onClick={() => setConfirming(selectedClaims)}
            >
              Confirm selected ({selectedClaims.length})
            </Button>
          ) : (
            <ButtonLink
              href={`/organizations/${organization.slug}`}
              target="_blank"
              variant="default"
            >
              View public page
              <ExternalLink className="ml-1.5 inline size-4" />
            </ButtonLink>
          )
        }
      />
    </div>
  );

  if (claims.length === 0) {
    return (
      <div className="space-y-8">
        {header}
        <EmptyState
          icon={ShieldCheck}
          title="No claims yet"
          description={`No coaches on Dream Coach Match with a public profile list ${organization.name} or its programs yet.`}
        >
          <ButtonLink
            href={`/organizations/${organization.slug}`}
            target="_blank"
            variant="default"
          >
            View your public page
          </ButtonLink>
        </EmptyState>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <ConfirmClaimsDialog
        claims={confirming.map(toDialogClaim)}
        decider={organization.name}
        onOpenChange={(open) => !open && setConfirming([])}
        onConfirm={(verificationDate) =>
          decide.mutateAsync({
            claims: confirming,
            decision: ClaimVerificationState.Verified,
            verificationDate,
            note: null,
          })
        }
      />
      <NotConfirmDialog
        claim={rejecting ? toDialogClaim(rejecting) : null}
        organization={organization.name}
        onOpenChange={(open) => !open && setRejecting(null)}
        onConfirm={(note) =>
          rejecting
            ? decide.mutateAsync({
                claims: [rejecting],
                decision: ClaimVerificationState.NotConfirmed,
                verificationDate: null,
                note,
              })
            : Promise.resolve()
        }
      />

      {header}

      <div className="space-y-4">
        <Tabs
          value={tab}
          onValueChange={(value) => {
            setTab(value as Tab);
            setSelected(new Set());
          }}
        >
          <TabsList>
            {TABS.map(({ value, label }) => (
              <TabsTrigger key={value} value={value}>
                {label} · {counts.get(value) ?? 0}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>

        <div
          className="flex flex-wrap gap-2"
          role="group"
          aria-label="Filter claims"
        >
          {chips.map((chip) => (
            <button
              key={chip.value}
              type="button"
              aria-pressed={filter === chip.value}
              onClick={() => {
                setFilter(chip.value);
                setSelected(new Set());
              }}
              className={cn(
                "rounded-full border px-3 py-1 text-sm transition-colors",
                filter === chip.value
                  ? "border-purple-200 bg-purple-50 text-purple-700"
                  : "text-text-secondary border-gray-200 bg-white hover:border-gray-300",
              )}
            >
              {chip.label}
            </button>
          ))}
        </div>

        {visible.length === 0 ? (
          <Card className="text-text-tertiary text-sm">
            {tab === ClaimVerificationState.Waiting
              ? "Nothing to review here."
              : tab === ClaimVerificationState.Verified
                ? "No confirmed claims here yet."
                : "No claims marked not confirmed here."}
          </Card>
        ) : (
          <Card className="gap-0 divide-y divide-gray-100 p-0">
            {visible.map((claim) => {
              const key = claimKey(claim);
              return (
                <div
                  key={key}
                  className="flex flex-wrap items-center gap-x-4 gap-y-2 px-4 py-3"
                >
                  {tab === ClaimVerificationState.Waiting && (
                    <Checkbox
                      aria-label={`Select ${claim.coachName} · ${claim.label}`}
                      checked={selected.has(key)}
                      onCheckedChange={() => toggle(key)}
                    />
                  )}
                  <div className="min-w-0 flex-1 basis-48">
                    <div className="text-base font-medium text-gray-900">
                      {claim.coachName}
                    </div>
                    <div className="text-text-tertiary text-xs">
                      {claim.coachCity && `${claim.coachCity} · `}
                      <a
                        href={`/${claim.coachUsername}`}
                        target="_blank"
                        rel="noreferrer"
                        className="underline underline-offset-2 hover:text-purple-700"
                      >
                        Profile
                      </a>
                    </div>
                  </div>
                  <div className="text-text-secondary min-w-0 basis-48 text-sm">
                    {claim.label}
                    {claim.joinedThroughInvite && (
                      <div className="text-text-tertiary text-xs">
                        Joined through your invite
                      </div>
                    )}
                  </div>
                  <div className="text-text-tertiary basis-40 text-sm">
                    {tab === ClaimVerificationState.Waiting
                      ? sinceText(claim)
                      : claim.state === ClaimVerificationState.Verified &&
                          claim.decider
                        ? verifiedByText(claim.decider, claim.verificationDate)
                        : claim.decisionNote
                          ? `Note: ${claim.decisionNote}`
                          : "No note"}
                  </div>
                  <div className="flex gap-2">
                    {claim.state !== ClaimVerificationState.Verified && (
                      <Button
                        size="sm"
                        onClick={() => setConfirming([claim])}
                        disabled={decide.isPending}
                      >
                        Confirm
                      </Button>
                    )}
                    {claim.state !== ClaimVerificationState.NotConfirmed && (
                      <Button
                        size="sm"
                        variant="destructive-ghost"
                        onClick={() => setRejecting(claim)}
                        disabled={decide.isPending}
                      >
                        Not ours
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
          </Card>
        )}
        <p className="text-text-tertiary text-sm">
          Coaches without a public profile are not listed. Dream Coach Match
          decides claims of coaches who also represent your organization.
        </p>
      </div>
    </div>
  );
}
